/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Power, Key, X, Storefront, FirstAid, Truck,
  MagnifyingGlass, CheckCircle, Copy, Check,
} from "@phosphor-icons/react";

const CAT_META = {
  retail:   { Icon: Storefront, color: "#0B2B66", label: "Retail" },
  health:   { Icon: FirstAid,   color: "#2D5A27", label: "Health" },
  delivery: { Icon: Truck,      color: "#C25D38", label: "Delivery" },
};

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function ServicePartnersAdmin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [tax, setTax] = useState(null);
  const [filter, setFilter] = useState({ category: "", subcategory: "", q: "" });
  const [editing, setEditing] = useState(null);    // existing partner record
  const [creating, setCreating] = useState(false);
  const [credDialog, setCredDialog] = useState(null); // {email, temporary_password}

  const loadTax = async () => {
    try {
      const r = await api.get("/admin/service-partners/taxonomy");
      setTax(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.category) params.set("category", filter.category);
      if (filter.subcategory) params.set("subcategory", filter.subcategory);
      if (filter.q) params.set("q", filter.q);
      const r = await api.get(`/admin/service-partners?${params}`);
      setRows(r.data.partners || []);
      setStats(r.data.stats || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadTax(); }, []);
  useEffect(() => { load(); }, [filter]);

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/service-partners/${p.id}`, { is_active: !p.is_active });
      toast.success(`${p.name} ${p.is_active ? "deactivated" : "reactivated"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPwd = async (p) => {
    if (!window.confirm(`Generate a new temporary password for ${p.name}? The old password will stop working.`)) return;
    try {
      const r = await api.post(`/admin/service-partners/${p.id}/reset-password`);
      setCredDialog(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Service Partners" subtitle="Administration · Retail / Health / Delivery"
      actions={
        <button data-testid="btn-new-partner" onClick={() => setCreating(true)}
                className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
          <Plus size={14} weight="bold"/> New partner
        </button>
      }>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="partner-stats">
        <Stat label="Total partners" value={stats?.total || 0}/>
        <Stat label="Active" value={stats?.active || 0} color="#2D5A27"/>
        <Stat label="Retail" value={stats?.by_category?.retail || 0} color="#0B2B66"/>
        <Stat label="Health · Delivery"
              value={(stats?.by_category?.health || 0) + (stats?.by_category?.delivery || 0)}
              color="#C25D38"/>
      </div>

      {/* Filter bar */}
      <div className="flex gap-2 mb-4 flex-wrap items-center" data-testid="partner-filters">
        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-3 top-3 text-[#8A8780]"/>
          <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                 data-testid="partner-search"
                 placeholder="Name, business, email or phone"
                 className="h-9 pl-9 pr-3 border border-[#E5E2D9] rounded-md bg-white text-sm w-64"/>
        </div>
        <select value={filter.category} data-testid="filter-category"
                onChange={(e) => setFilter({ ...filter, category: e.target.value, subcategory: "" })}
                className="h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
          <option value="">All categories</option>
          {(tax?.categories || []).map(c => <option key={c} value={c}>{CAT_META[c]?.label || c}</option>)}
        </select>
        <select value={filter.subcategory} data-testid="filter-subcategory"
                onChange={(e) => setFilter({ ...filter, subcategory: e.target.value })}
                disabled={!filter.category}
                className="h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm disabled:opacity-50">
          <option value="">All subcategories</option>
          {filter.category && tax && (tax.by_category[filter.category] || []).map(s =>
            <option key={s} value={s}>{prettyLabel(s)}</option>)}
        </select>
        {(filter.category || filter.subcategory || filter.q) && (
          <button onClick={() => setFilter({ category: "", subcategory: "", q: "" })}
                  className="text-xs text-[#5C5A55] underline hover:text-[#2D5A27]">
            Clear
          </button>
        )}
      </div>

      {/* Partner table */}
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="partner-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">Partner</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Subcategory</th>
              <th className="px-4 py-3">Phone / Email</th>
              <th className="px-4 py-3">Radius</th>
              <th className="px-4 py-3">Commission</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-[#8A8780]">No partners yet — click "New partner" to add one.</td></tr>
            )}
            {rows.map((p, i) => {
              const meta = CAT_META[p.category] || CAT_META.retail;
              return (
                <tr key={p.id} data-testid={`partner-row-${p.id}`}
                    className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A1A1A]">{p.name}</div>
                    {p.business_name && <div className="text-[11px] text-[#5C5A55]">{p.business_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs"
                          style={{ color: meta.color }}>
                      <meta.Icon size={12} weight="duotone"/>{meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5C5A55]">{prettyLabel(p.subcategory)}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-mono">{p.phone}</div>
                    <div className="text-[11px] text-[#5C5A55] truncate max-w-[200px]">{p.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{p.service_radius_km} km</td>
                  <td className="px-4 py-3 text-xs font-mono">{p.commission_pct}%</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${
                      p.is_active ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#E5E2D9] text-[#5C5A55]"
                    }`}>
                      {p.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`}
                              title="Edit partner"
                              className="h-8 w-8 grid place-items-center text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
                        <Pencil size={12}/>
                      </button>
                      <button onClick={() => resetPwd(p)} data-testid={`reset-${p.id}`}
                              title="Reset partner password"
                              className="h-8 w-8 grid place-items-center text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
                        <Key size={12}/>
                      </button>
                      <button onClick={() => toggleActive(p)} data-testid={`toggle-${p.id}`}
                              title={p.is_active ? "Deactivate" : "Reactivate"}
                              className={`h-8 w-8 grid place-items-center rounded ${
                                p.is_active ? "text-[#C25D38] hover:bg-[#FBE7E0]" : "text-[#2D5A27] hover:bg-[#E1EFDE]"
                              }`}>
                        <Power size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <PartnerForm tax={tax}
                      onClose={() => setCreating(false)}
                      onCreated={(creds) => { setCreating(false); load(); setCredDialog(creds); }}/>
      )}
      {editing && (
        <PartnerForm tax={tax} editing={editing}
                      onClose={() => setEditing(null)}
                      onCreated={() => { setEditing(null); load(); }}/>
      )}
      {credDialog && (
        <CredentialDialog data={credDialog} onClose={() => setCredDialog(null)}/>
      )}
    </AppShell>
  );
}

// ============================================================
// Create / Edit partner modal
// ============================================================
function PartnerForm({ editing, tax, onClose, onCreated }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    name: editing?.name || "",
    business_name: editing?.business_name || "",
    phone: editing?.phone || "",
    email: editing?.email || "",
    gst_no: editing?.gst_no || "",
    address: editing?.address || "",
    lat: editing?.lat || "",
    lng: editing?.lng || "",
    category: editing?.category || "retail",
    subcategory: editing?.subcategory || "pharmacy",
    service_radius_km: editing?.service_radius_km ?? 10,
    service_mode: editing?.service_mode || "both",
    appointment_slot_minutes: editing?.appointment_slot_minutes ?? 30,
    commission_pct: editing?.commission_pct ?? 10,
    hours_open: editing?.hours_open || "09:00",
    hours_close: editing?.hours_close || "21:00",
    days_open: editing?.days_open || ["mon","tue","wed","thu","fri","sat"],
    delivery_rate_per_km: editing?.delivery_rate_per_km ?? 30,
    min_order_amount: editing?.min_order_amount ?? 500,
    convenience_charge: editing?.convenience_charge ?? 100,
    home_delivery: editing?.home_delivery ?? false,
    delivery_time_text: editing?.delivery_time_text || "",
    offer_text: editing?.offer_text || "",
    highlight_text: editing?.highlight_text || "",
    attached_hospital_ids: editing?.attached_hospital_ids || [],
    any_hospital: editing?.any_hospital ?? false,
    vehicle_class: editing?.vehicle_class || "",
    equipment_list: editing?.equipment_list || [],
    subscription_plan_id: editing?.subscription_plan_id || "",
    admin_login_email: "",
    admin_login_name: "",
  }));
  const [busy, setBusy] = useState(false);

  // When category changes during create, reset subcategory to a valid default
  const subOpts = useMemo(() => {
    if (!tax) return [];
    return tax.by_category[form.category] || [];
  }, [tax, form.category]);
  useEffect(() => {
    if (!isEdit && !subOpts.includes(form.subcategory) && subOpts[0]) {
      setForm(f => ({ ...f, subcategory: subOpts[0] }));
    }
    // eslint-disable-next-line
  }, [form.category, subOpts]);

  const toggleDay = (d) => {
    setForm(f => ({
      ...f,
      days_open: f.days_open.includes(d)
        ? f.days_open.filter(x => x !== d)
        : [...f.days_open, d],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.service_radius_km = Number(payload.service_radius_km) || 0;
      payload.commission_pct = Number(payload.commission_pct) || 0;
      payload.appointment_slot_minutes = Number(payload.appointment_slot_minutes) || 30;
      payload.delivery_rate_per_km = Number(payload.delivery_rate_per_km) || 0;
      payload.min_order_amount = Number(payload.min_order_amount) || 0;
      payload.convenience_charge = Number(payload.convenience_charge) || 0;
      payload.lat = payload.lat === "" ? null : Number(payload.lat);
      payload.lng = payload.lng === "" ? null : Number(payload.lng);
      if (!payload.admin_login_email) delete payload.admin_login_email;
      if (!payload.admin_login_name) delete payload.admin_login_name;
      if (!payload.subscription_plan_id) delete payload.subscription_plan_id;
      if (isEdit) {
        // Strip create-only fields
        delete payload.admin_login_email;
        delete payload.admin_login_name;
        delete payload.email;
        delete payload.category;
        delete payload.subcategory;
        await api.put(`/admin/service-partners/${editing.id}`, payload);
        toast.success("Partner updated");
        onCreated();
      } else {
        const r = await api.post(`/admin/service-partners`, payload);
        toast.success("Partner created");
        onCreated(r.data.initial_login);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto"
          onClick={onClose} data-testid="partner-form">
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-2xl my-8 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A8780]">
              {isEdit ? "Edit Service Partner" : "Create Service Partner"}
            </div>
            <div className="font-heading text-xl">{isEdit ? form.name : "New partner"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded">
            <X size={16}/>
          </button>
        </div>

        <Section title="Basic">
          <Field label="Partner name *" testid="f-name">
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                   className={inputCls}/>
          </Field>
          <Field label="Business / legal name" testid="f-business">
            <input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})}
                   className={inputCls}/>
          </Field>
          <Field label="Phone *" testid="f-phone">
            <input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                   className={inputCls}/>
          </Field>
          <Field label="Email *" testid="f-email">
            <input required type="email" value={form.email} disabled={isEdit}
                    onChange={e => setForm({...form, email: e.target.value})}
                    className={`${inputCls} disabled:bg-[#F7F6F2] disabled:text-[#8A8780]`}/>
          </Field>
          <Field label="GST number" testid="f-gst">
            <input value={form.gst_no} onChange={e => setForm({...form, gst_no: e.target.value})}
                   className={inputCls}/>
          </Field>
          <Field label="Address">
            <input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                   className={inputCls}/>
          </Field>
          <Field label="Latitude">
            <input type="number" step="any" value={form.lat} onChange={e => setForm({...form, lat: e.target.value})}
                   className={inputCls}/>
          </Field>
          <Field label="Longitude">
            <input type="number" step="any" value={form.lng} onChange={e => setForm({...form, lng: e.target.value})}
                   className={inputCls}/>
          </Field>
        </Section>

        <Section title="Category">
          <Field label="Category *" testid="f-category">
            <select value={form.category} disabled={isEdit}
                    onChange={e => setForm({...form, category: e.target.value})}
                    className={`${inputCls} disabled:bg-[#F7F6F2]`}>
              {(tax?.categories || ["retail", "health", "delivery"]).map(c =>
                <option key={c} value={c}>{CAT_META[c]?.label || c}</option>)}
            </select>
          </Field>
          <Field label="Subcategory *" testid="f-subcategory">
            <select value={form.subcategory} disabled={isEdit}
                    onChange={e => setForm({...form, subcategory: e.target.value})}
                    className={`${inputCls} disabled:bg-[#F7F6F2]`}>
              {subOpts.map(s => <option key={s} value={s}>{prettyLabel(s)}</option>)}
            </select>
          </Field>
          {isEdit && (
            <div className="col-span-2 text-[11px] text-[#8A8780] italic">
              Category &amp; subcategory are locked after creation — they affect commerce,
              billing &amp; routing. Create a new partner record if the business changes type.
            </div>
          )}
        </Section>

        <Section title="Operations">
          <Field label="Service mode" testid="f-service-mode">
            <select value={form.service_mode}
                    onChange={e => setForm({...form, service_mode: e.target.value})}
                    className={inputCls}>
              <option value="on_premise">On-premise only</option>
              <option value="doorstep">Doorstep only</option>
              <option value="both">Both</option>
            </select>
          </Field>
          <Field label="Service radius (km)" testid="f-radius">
            <input type="number" step="0.5" min="0" value={form.service_radius_km}
                    onChange={e => setForm({...form, service_radius_km: e.target.value})}
                    className={inputCls}/>
          </Field>
          <Field label="Appointment slot (min)">
            <input type="number" min="5" value={form.appointment_slot_minutes}
                    onChange={e => setForm({...form, appointment_slot_minutes: e.target.value})}
                    className={inputCls}/>
          </Field>
          <Field label="Commission %" testid="f-commission">
            <input type="number" step="0.5" min="0" max="100" value={form.commission_pct}
                    onChange={e => setForm({...form, commission_pct: e.target.value})}
                    className={inputCls}/>
          </Field>
          <Field label="Hours · Open">
            <input type="time" value={form.hours_open}
                    onChange={e => setForm({...form, hours_open: e.target.value})}
                    className={inputCls}/>
          </Field>
          <Field label="Hours · Close">
            <input type="time" value={form.hours_close}
                    onChange={e => setForm({...form, hours_close: e.target.value})}
                    className={inputCls}/>
          </Field>
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">Operating days</div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map(d => {
                const on = form.days_open.includes(d);
                return (
                  <button type="button" key={d} onClick={() => toggleDay(d)}
                          data-testid={`day-${d}`}
                          className={`px-3 py-1 text-xs uppercase tracking-[0.15em] rounded border ${
                            on ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                          }`}>
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        <Section title="Delivery pricing (home-service orders)">
          <Field label="Delivery rate ₹/km" testid="f-delivery-rate"
                  hint="Applied per kilometre from partner location to customer">
            <input type="number" step="1" min="0" value={form.delivery_rate_per_km}
                    onChange={e => setForm({...form, delivery_rate_per_km: e.target.value})}
                    data-testid="f-delivery-rate-input"
                    className={inputCls}/>
          </Field>
          <Field label="Min. order ₹" testid="f-min-order"
                  hint="Orders below this add a convenience charge">
            <input type="number" step="1" min="0" value={form.min_order_amount}
                    onChange={e => setForm({...form, min_order_amount: e.target.value})}
                    data-testid="f-min-order-input"
                    className={inputCls}/>
          </Field>
          <Field label="Convenience charge ₹" testid="f-convenience"
                  hint="Added only when order value is below the minimum">
            <input type="number" step="1" min="0" value={form.convenience_charge}
                    onChange={e => setForm({...form, convenience_charge: e.target.value})}
                    data-testid="f-convenience-input"
                    className={inputCls}/>
          </Field>
          <div className="col-span-2 text-[11px] text-[#5C5A55] leading-relaxed bg-[#F7F6F2] rounded p-2.5">
            Defaults: <b>₹30/km</b> delivery · <b>₹500</b> minimum order · <b>₹100</b> convenience charge for smaller orders.
          </div>
          <Field label="Home delivery" testid="f-home-delivery" hint="Shows a 'Home delivery' badge to customers">
            <label className="flex items-center gap-2 h-9 text-sm">
              <input type="checkbox" checked={!!form.home_delivery} onChange={e => setForm({...form, home_delivery: e.target.checked})} data-testid="f-home-delivery-input"/>
              Offers home delivery
            </label>
          </Field>
          <Field label="Delivery time" testid="f-delivery-time" hint="Used when road time can't be calculated (default 40–60 mins)">
            <input value={form.delivery_time_text || ""} onChange={e => setForm({...form, delivery_time_text: e.target.value})}
                    placeholder="40–60 mins" data-testid="f-delivery-time-input" className={inputCls}/>
          </Field>
          <Field label="Offer line" testid="f-offer" hint="e.g. 10% off on selected items">
            <input value={form.offer_text || ""} onChange={e => setForm({...form, offer_text: e.target.value})}
                    data-testid="f-offer-input" className={inputCls}/>
          </Field>
          <Field label="Highlight" testid="f-highlight" hint="e.g. Wide range of medicines">
            <input value={form.highlight_text || ""} onChange={e => setForm({...form, highlight_text: e.target.value})}
                    data-testid="f-highlight-input" className={inputCls}/>
          </Field>
        </Section>

        {form.subcategory === "ambulance" && (
          <Section title="Ambulance service">
            <Field label="Vehicle class" testid="f-vehicle-class"
                    hint="e.g. BLS, ALS, ACLS, Air Ambulance, Neonatal, Mortuary">
              <select value={form.vehicle_class || ""}
                       onChange={e => setForm({ ...form, vehicle_class: e.target.value })}
                       data-testid="f-vehicle-class-input"
                       className={inputCls}>
                <option value="">— Select —</option>
                <option value="BLS">BLS · Basic Life Support</option>
                <option value="ALS">ALS · Advanced Life Support</option>
                <option value="ACLS">ACLS · Advanced Cardiac</option>
                <option value="Air Ambulance">Air Ambulance</option>
                <option value="Neonatal">Neonatal / NICU</option>
                <option value="Mortuary">Mortuary Van</option>
              </select>
            </Field>
            <Field label="Equipment (comma-separated)" testid="f-equipment"
                    hint="e.g. Oxygen, Defibrillator, Ventilator, Stretcher, ECG">
              <input type="text"
                      value={(form.equipment_list || []).join(", ")}
                      onChange={e => setForm({ ...form, equipment_list: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                      data-testid="f-equipment-input"
                      className={inputCls}/>
            </Field>
            <div className="col-span-2">
              <AttachHospitalsField
                selectedIds={form.attached_hospital_ids || []}
                anyHospital={!!form.any_hospital}
                onSelected={(ids) => setForm({ ...form, attached_hospital_ids: ids })}
                onAnyToggle={(v) => setForm({ ...form, any_hospital: v })}
              />
            </div>
          </Section>
        )}

        {!isEdit && (
          <Section title="Initial login (optional)">
            <Field label="Login email"
                    hint="Defaults to partner email if blank">
              <input type="email" value={form.admin_login_email}
                      onChange={e => setForm({...form, admin_login_email: e.target.value})}
                      className={inputCls}/>
            </Field>
            <Field label="Login user name">
              <input value={form.admin_login_name}
                      onChange={e => setForm({...form, admin_login_name: e.target.value})}
                      className={inputCls}/>
            </Field>
            <div className="col-span-2 text-[11px] text-[#5C5A55] bg-[#F7F6F2] border border-[#E5E2D9] rounded px-3 py-2">
              A temporary password will be generated and shown once on the next screen — copy
              and share it with the partner. They'll be prompted to change it on first login.
            </div>
          </Section>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F0ECE3]">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 rounded border border-[#E5E2D9] text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="partner-submit"
                  className="h-10 px-5 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium disabled:opacity-60">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create partner"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CredentialDialog({ data, onClose }) {
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={onClose} data-testid="cred-dialog">
      <div className="bg-white rounded-md w-full max-w-md p-6 space-y-5"
            onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 grid place-items-center rounded-full bg-[#E1EFDE] text-[#2D5A27]">
            <CheckCircle size={18} weight="fill"/>
          </div>
          <div>
            <div className="font-heading text-xl">Partner credentials</div>
            <div className="text-xs text-[#5C5A55] mt-0.5">
              Share these securely with the partner. This is the only time the password
              will be displayed.
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <Row label="Login email" v={data.email} onCopy={copy}/>
          <Row label="Temporary password" v={data.temporary_password} mono onCopy={copy}/>
        </div>
        <div className="text-[11px] text-[#5C5A55] bg-[#F9F4D9] border border-[#E5DBA0] rounded px-3 py-2">
          The partner will be prompted to change this password on first login. They'll
          also be emailed a one-time OTP for two-step verification on every sign-in.
        </div>
        <button onClick={onClose} data-testid="cred-close"
                className="w-full h-10 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium">
          Done
        </button>
      </div>
    </div>
  );
}

function Row({ label, v, mono, onCopy }) {
  return (
    <div className="border border-[#E5E2D9] rounded p-2.5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{label}</div>
        <div className={`mt-0.5 truncate ${mono ? "font-mono text-[#1A1A1A]" : "text-sm"}`}>{v}</div>
      </div>
      <button onClick={() => onCopy(v)} className="h-8 w-8 grid place-items-center text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
        <Copy size={12}/>
      </button>
    </div>
  );
}

const inputCls = "w-full h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]";

function AttachHospitalsField({ selectedIds, anyHospital, onSelected, onAnyToggle }) {
  const [q, setQ] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);

  // Fetch hospital lookup as the query changes
  React.useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/hospitals/lookup", { params: { q, limit: 30 } });
        setRows(r.data.hospitals || []);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // Hydrate `selectedRows` from IDs once on mount
  React.useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) { setSelectedRows([]); return; }
    api.get("/admin/hospitals").then(r => {
      const map = new Map((r.data.hospitals || []).map(h => [h.id, h]));
      setSelectedRows(selectedIds.map(id => map.get(id)).filter(Boolean));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const toggle = (h) => {
    if (selectedIds.includes(h.id)) {
      const next = selectedIds.filter(x => x !== h.id);
      onSelected(next);
      setSelectedRows(sr => sr.filter(s => s.id !== h.id));
    } else {
      onSelected([...selectedIds, h.id]);
      setSelectedRows(sr => [...sr, h]);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">
        Attached hospitals
      </div>
      <label className="flex items-center gap-2 text-[13px] mb-3 cursor-pointer p-2.5 border border-[#E5E2D9] rounded-md hover:bg-slate-50"
             data-testid="f-any-hospital">
        <input type="checkbox" checked={anyHospital}
                onChange={e => onAnyToggle(e.target.checked)}
                className="accent-[#DC2626]"/>
        <div>
          <div className="font-semibold text-slate-900">Serve ANY hospital</div>
          <div className="text-[11px] text-slate-500">Skip attachment — this ambulance can drop patients to any hospital in the directory.</div>
        </div>
      </label>

      {!anyHospital && (
        <>
          <div className="relative">
            <MagnifyingGlass size={12} weight="duotone"
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Search hospitals to attach…"
                    data-testid="f-hosp-search"
                    className={inputCls + " pl-8"}/>
          </div>

          {selectedRows.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="f-hosp-selected">
              {selectedRows.map(h => (
                <span key={h.id} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-[#DC2626] text-white text-[11px] font-semibold">
                  {h.name}
                  <button type="button" onClick={() => toggle(h)}
                           className="w-5 h-5 rounded-full hover:bg-white/20 inline-flex items-center justify-center">
                    <X size={9} weight="bold"/>
                  </button>
                </span>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded-md max-h-56 overflow-auto">
              {rows.map(h => {
                const on = selectedIds.includes(h.id);
                return (
                  <button type="button" key={h.id} onClick={() => toggle(h)}
                           data-testid={`f-hosp-opt-${h.id}`}
                           className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 ${on ? "bg-[#FEF2F2]" : ""}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{h.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {[h.speciality, h.district, h.state].filter(Boolean).join(" · ")}
                        {h.is_super_speciality && " · Super speciality"}
                        {h.has_trauma_care && " · Trauma"}
                        {h.has_air_ambulance && " · Air"}
                      </div>
                    </div>
                    {on ? <Check size={13} weight="bold" className="text-[#DC2626]"/>
                        : <Plus size={13} weight="bold" className="text-slate-400"/>}
                  </button>
                );
              })}
            </div>
          )}
          {rows.length === 0 && q && (
            <div className="mt-2 text-[11px] text-slate-500">No hospitals match &ldquo;{q}&rdquo;. Add them from the Hospital Directory.</div>
          )}
        </>
      )}
    </div>
  );
}


const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55] mb-3">{title}</div>
    <div className="grid grid-cols-2 gap-3">{children}</div>
  </div>
);
const Field = ({ label, hint, testid, children }) => (
  <label className="block" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-[#8A8780] mt-1">{hint}</div>}
  </label>
);
const Stat = ({ label, value, color }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className="font-heading text-3xl mt-1" style={{ color: color || "#1A1A1A" }}>{value}</div>
  </div>
);

function prettyLabel(s) {
  if (!s) return "";
  return s.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
